import { useState } from 'react'
import { Button, Input, ScrollArea, Badge } from '@creanizer/ui'
import { Search, Trash2, Eye, Edit, Download, Castle, Folder } from 'lucide-react'
import type { SavedDungeon } from '@creanizer/types'
import { useDungeonStore } from '../../../../stores/dungeonStore'
import { useFolderStore } from '../../../../stores/folderStore'
import { ExportImportButtons } from '../../../../components/common'
import { DungeonEditModal } from './DungeonEditModal'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface DungeonSavedProps {
  dungeons: SavedDungeon[]
  onLoad: (dungeon: SavedDungeon) => void
}

export function DungeonSaved({ dungeons, onLoad }: DungeonSavedProps) {
  const { 
    removeDungeon, 
    clearAll, 
    exportAsText, 
    exportAsJSON, 
    exportSingleAsText, 
    exportSingleAsJSON, 
    importFromJSON, 
    updateDungeon 
  } = useDungeonStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [editingDungeon, setEditingDungeon] = useState<SavedDungeon | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let folderFilteredDungeons = dungeons
  if (selectedFolderId === 'all') {
    folderFilteredDungeons = dungeons
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('dungeon')
    folders.forEach(folder => {
      getItemsInFolder('dungeon', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredDungeons = dungeons.filter(dungeon => !allFolderItemIds.has(dungeon.id))
  } else {
    const itemIds = getItemsInFolder('dungeon', selectedFolderId)
    folderFilteredDungeons = dungeons.filter(dungeon => itemIds.includes(dungeon.id))
  }
  
  // 検索によるフィルタリング
  const filteredDungeons = folderFilteredDungeons.filter(dungeon =>
    dungeon.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dungeon.result.basicInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dungeon.result.basicInfo.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dungeon.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  )
  
  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <Castle className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済みダンジョン ({dungeons.length}/200)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成したダンジョン設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="ダンジョンを検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={exportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="ダンジョン"
          />

          {dungeons.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべてのダンジョンを削除しますか？')) {
                  clearAll()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="dungeon"
                items={dungeons}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('dungeon', itemId)
                  moveItemBetweenFolders('dungeon', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* ダンジョンリスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredDungeons.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchTerm ? '該当するダンジョンが見つかりません' : 'まだダンジョンが保存されていません'}
                  </p>
                ) : (
                  filteredDungeons.map((dungeon) => (
                    <div 
                      key={dungeon.id} 
                      className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                      draggable
                      onDragStart={(e) => {
                        setDraggedItemId(dungeon.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setDraggedItemId(null)
                      }}
                      style={{
                        opacity: draggedItemId === dungeon.id ? 0.5 : 1
                      }}
                    >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{dungeon.title}</h3>
                        {dungeon.favorite && <Badge variant="secondary" className="text-xs">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{dungeon.result.basicInfo.type}</Badge>
                        <Badge variant="outline" className="text-xs">難易度: {dungeon.result.basicInfo.difficulty}</Badge>
                        <Badge variant="outline" className="text-xs">フロア: {dungeon.result.structure.floors.length}</Badge>
                        <Badge variant="outline" className="text-xs">部屋: {dungeon.result.structure.totalRooms}</Badge>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {dungeon.result.basicInfo.summary}
                      </p>
                      
                      {dungeon.tags && dungeon.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {dungeon.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {dungeon.memo && (
                        <p className="text-xs text-muted-foreground mt-2 truncate">
                          メモ: {dungeon.memo}
                        </p>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(dungeon.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2">
                        <FolderMoveSelect
                          generatorType="dungeon"
                          itemId={dungeon.id}
                          currentFolderId={getItemFolder('dungeon', dungeon.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad(dungeon)}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingDungeon(dungeon)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsText(dungeon.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsJSON(dungeon.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このダンジョンを削除しますか？')) {
                            removeDungeon(dungeon.id)
                          }
                        }}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{dungeon.title}</h3>
                        {dungeon.favorite && <Badge variant="secondary" className="text-xs">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{dungeon.result.basicInfo.type}</Badge>
                        <Badge variant="outline" className="text-xs">難易度: {dungeon.result.basicInfo.difficulty}</Badge>
                        <Badge variant="outline" className="text-xs">フロア: {dungeon.result.structure.floors.length}</Badge>
                        <Badge variant="outline" className="text-xs">部屋: {dungeon.result.structure.totalRooms}</Badge>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {dungeon.result.basicInfo.summary}
                      </p>
                      
                      {dungeon.tags && dungeon.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {dungeon.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {dungeon.memo && (
                        <p className="text-xs text-muted-foreground mt-2 truncate">
                          メモ: {dungeon.memo}
                        </p>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(dungeon.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択（PC版） */}
                      <div className="mt-2 w-32">
                        <FolderMoveSelect
                          generatorType="dungeon"
                          itemId={dungeon.id}
                          currentFolderId={getItemFolder('dungeon', dungeon.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onLoad(dungeon)}
                          title="読込"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingDungeon(dungeon)}
                          title="編集"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          編集
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(dungeon.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          <span className="text-xs">txt</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(dungeon.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          <span className="text-xs">json</span>
                        </Button>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm('このダンジョンを削除しますか？')) {
                            removeDungeon(dungeon.id)
                          }
                        }}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
      
      {editingDungeon && (
        <DungeonEditModal
          dungeon={editingDungeon}
          onClose={() => setEditingDungeon(null)}
        />
      )}
    </div>
  )
}